import classNames from "classnames";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

import Button from "components/button/Button";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { DropdownMenu } from "components/dropdown-menu/DropdownMenu";
import ToggleablePanel from "components/header/ToggleablePanel";
import ExportIcon from "components/icons/ExportIcon";
import Info from "components/icons/Info";
import KebabIcon from "components/icons/KebabIcon";
import CircleNumber from "components/products/CircleWithNumber";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import { ALL_MCS_ROUTE } from "components/router/Routes";
import Tooltip from "components/tooltip/Tooltip";
import { BmpOpOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { OnboardingImage } from "domain/softwarePackages";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { createShaChecksumMenuItem } from "utils/shaChecksumUtils";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepIndex: number;
    menuExpanded: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
    package?: OnboardingImage;
}

interface Step {
    description: string;
    action?: JSX.Element;
}

const InstallBmpOp = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [showContent, setShowContent] = React.useState<boolean>(false);
    const stepName = BmpOpOnboardingSteps.STEP_BMPOP_INSTALLATION;

    const clickEvent = () => {
        props.stepClickable && setShowContent(!showContent);
    };

    const saveOnboardingStep = () => {
        props.saveOnboardingStep({
            product: OnboardingProductType.BMPOP,
            step: BmpOpOnboardingSteps.STEP_BMPOP_INSTALLATION,
        });
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const haspBanner = (textToShow: string) => {
        return (
            <div className={style.alertBox}>
                <div>
                    <Info borderColor={theme.contentBackgroundColor} color={theme.sortIconActiveColor} size={"18"} />
                </div>
                <div className={style.bannerText}>{textToShow}</div>
            </div>
        );
    };
    const howToRegisterStep = () => {
        return (
            <div>
                <div>
                    <ul>
                        <li className={style.listPadding}>
                            <Trans
                                i18nKey={t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.bulletOne")}
                                values={{
                                    linkText: t(
                                        "Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.onPremiseLinkText"
                                    ),
                                }}
                                components={[
                                    <a
                                        key="link"
                                        onClick={() => {
                                            window.location.href = ALL_MCS_ROUTE.path;
                                        }}
                                    />,
                                ]}
                            />
                        </li>
                        <li className={style.listPadding}>
                            {t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.bulletTwo")}
                        </li>
                    </ul>
                </div>
                <div>{haspBanner(t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.note"))}</div>
            </div>
        );
    };

    function createDownloadContainer(withSmallerWidth: boolean) {
        return (
            <div
                className={classNames(style.stepActionButton, style.stepContainer, {
                    [style.smallerWidth]: withSmallerWidth,
                })}
            >
                <div> {t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.downloadFileName")}</div>
                <div className={style.stepActionButtonWrapper}>
                    <div className={style.actionButton}>
                        <Button
                            variant={"TERTIARY"}
                            onClick={() => {
                                const link = document.createElement("a");
                                if (props.package) {
                                    link.href = props.package.manual;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }
                            }}
                        >
                            {" "}
                            <ExportIcon size={24} color={theme.iconFillColor} />
                        </Button>
                    </div>
                    {props.package?.shaForManual && (
                        <div className={style.actionButton}>
                            <ToggleablePanel
                                title={""}
                                testId={testIds.workArea.onboarding.step.kebabMenu.button}
                                menuType={"HEADER"}
                                icon={
                                    <Tooltip content={t("Onboarding.home.downloadIconTooltip")}>
                                        <KebabIcon color={theme.iconFillColor} />
                                    </Tooltip>
                                }
                                hideChevron={true}
                            >
                                <DropdownMenu
                                    position={"AUTO"}
                                    testId={testIds.workArea.onboarding.step.kebabMenu.itself}
                                    menuItems={[
                                        createShaChecksumMenuItem(
                                            props.package.shaForManual,
                                            props.package.manualProductName,
                                            t("Onboarding.home.verifyDownloadFile"),
                                            0,
                                            t,
                                            props.theme.iconFillColor,
                                            props.theme.whiteColor
                                        ),
                                    ]}
                                />
                            </ToggleablePanel>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    const installBmpOpWindowsSteps: Step[] = [
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.windows.download"),
            action: createDownloadContainer(true),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.followSteps"),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.howToRegister"),
            action: howToRegisterStep(),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.installationHaspKeyMessage"),
        },
    ];

    function generateCommandToCopy() {
        const splitLink = props.package ? props.package.manual.split("/") : "";
        const filename = splitLink[splitLink.length - 1];
        return `unzip ${filename} && sudo java -jar ${filename.replace("zip", "jar")}`;
    }

    const installBmpOpLinuxSteps: Step[] = [
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.linux.download"),
            action: createDownloadContainer(false),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.linux.commandToDownload"),
            action: (
                <div className={classNames(style.stepActionButton, style.linuxCommandFont)}>
                    <div className={style.linuxCommandFont}> {generateCommandToCopy()}</div>
                    <div className={style.stepActionButtonWrapper}>
                        <div className={style.actionButton}>
                            <CopyToClipboard value={generateCommandToCopy()} />
                        </div>
                    </div>
                </div>
            ),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.followSteps"),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.howToRegister"),
            action: howToRegisterStep(),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.installationHaspKeyMessage"),
        },
    ];

    function renderSteps(steps: Step[]) {
        return (
            <div className={style.stepsContainer}>
                {haspBanner(t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.common.removeHasp"))}
                {steps.map((step, index) => (
                    <div key={index} className={style.subStepsToFollowLayout}>
                        <CircleNumber number={index + 1} subStep={true} />
                        <div className={style.stepPadding}>
                            <div>{step.description}</div>
                            {step.action}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    function createContent() {
        return (
            <>
                <Tabs>
                    <TabList>
                        <Tab>{t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.windows.tab")}</Tab>
                        <Tab>{t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.linux.tab")}</Tab>
                    </TabList>
                    <TabPanel>{renderSteps(installBmpOpWindowsSteps)}</TabPanel>
                    <TabPanel>{renderSteps(installBmpOpLinuxSteps)}</TabPanel>
                </Tabs>
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <StepCompletedText
                            successText={t("Onboarding.gettingStartedFlow.completed")}
                            testId={testIds.workArea.onboarding.step.statusLabel}
                            dataType={OnboardingProductType.BMPOP + "_" + stepName}
                        />
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={() => {
                                props.saveStepInProgressIndex(props.stepIndex);
                                saveOnboardingStep();
                            }}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={OnboardingProductType.BMPOP + "_" + stepName}
                        >
                            {t("Onboarding.gettingStartedFlow.markAsCompleted")}
                        </Button>
                    )}
                </div>
            </>
        );
    }

    return (
        <StepHeader
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            title={t("Onboarding.bmpOp.gettingStarted.steps.bmpOpInstallation.title")}
            stepIndex={props.stepIndex}
            menuExpanded={props.menuExpanded}
            showContent={showContent}
            clickEvent={clickEvent}
            content={createContent()}
            type={OnboardingProductType.BMPOP}
            stepName={stepName}
        />
    );
};

export default connector(InstallBmpOp);
